import { useState, useEffect } from "react";
import { Table, Input, Button, Modal, Pagination } from "antd";
import { CodeOutlined, FullscreenOutlined, FullscreenExitOutlined } from "@ant-design/icons";
import FontAwesome from "react-fontawesome";
import _ from "lodash";
import { DownloadCsv } from "../../../utils/csvUtils";


const SqlTable = (props) => {
  const [filterTable, setFilterTable] = useState(null);
  const [columns, setColumns] = useState(props.columns);
  const [baseData, setBaseData] = useState(props.data);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [queryModalVisible, setQueryModalVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Update state when props change
  useEffect(() => {
    setColumns(props.columns);
    setBaseData(props.data);
    setFilterTable(null); // Reset filter when data changes
    setCurrentPage(1); // Reset to first page when data changes
  }, [props.columns, props.data]);

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isFullscreen]);

  const onSearch = (value) => {
    const filteredData = baseData.filter(o =>
      Object.keys(o).some(k =>
        String(o[k])
          .toLowerCase()
          .includes(value.toLowerCase())
      )
    );

    setFilterTable(filteredData);
    setCurrentPage(1); // Reset to first page when searching
  };

  const downloadCsv = () => {
    const headerKeys = _.keys(_.get(baseData, "0", []));
    DownloadCsv(baseData, headerKeys, "result");
  };

  const showQueryModal = () => {
    setQueryModalVisible(true);
  };

  const hideQueryModal = () => {
    setQueryModalVisible(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Get current data (filtered or base)
  const currentData = filterTable || baseData;

  // Calculate pagination
  const totalRecords = currentData.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = currentData.slice(startIndex, endIndex);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    }
  };

  return (
    <div style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      ...(isFullscreen && {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: "#fff",
        padding: "16px"
      })
    }}>
      {/* Search Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "8px", flexShrink: 0 }}>
        <Input.Search
          placeholder="Search"
          enterButton
          allowClear
          onChange={(e) => onSearch(e.target.value)}
          style={{ maxWidth: "300px" }}
        />
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </Button>
          {props.query && (
            <Button
              icon={<CodeOutlined />}
              onClick={showQueryModal}
            >
              View Query
            </Button>
          )}
          <Button
            type="primary"
            onClick={downloadCsv}
          >
            <FontAwesome name="download" />
            Download CSV
          </Button>
        </div>
      </div>

      {/* Table and Pagination Wrapper */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Table */}
        <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          <Table
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
            size="small"
            // sticky
          />
        </div>

        {/* Separate Pagination */}
        <div style={{ flexShrink: 0, padding: "8px", display: "flex", justifyContent: "flex-end", borderTop: "1px solid #f0f0f0" }}>
          <Pagination
            current={currentPage}
            total={totalRecords}
            pageSize={pageSize}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} records`}
            pageSizeOptions={['10', '25', '50', '100']}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            size="small"
          />
        </div>
      </div>
      
      {/* Query Modal */}
      {props.query && (
        <Modal
          title="SQL Query"
          open={queryModalVisible}
          onCancel={hideQueryModal}
          footer={[
            <Button key="close" onClick={hideQueryModal}>
              Close
            </Button>,
          ]}
          width={700}
          zIndex={isFullscreen ? 10000 : 1000}
        >
          <div style={{
            fontSize: "12px",
            fontFamily: "'Courier New', Courier, monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            padding: "12px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            maxHeight: "400px",
            overflow: "auto",
          }}>
            {props.query}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SqlTable;
